import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { PrivacyState } from '@df/core/meta/privacy-state';
import { CookiesService } from '@df/core/service/cookies';
import { DfEventService } from '@df/core/service/df-event';
import { Logger } from '@df/core/service/logger';
import { ProcessorService } from '@df/ng/processor.service';
import { EAsyncPriority, TimeoutService } from '@df/ng/timeout.service';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import { Subject, Subscription } from 'rxjs';
import { NDfPrivacy } from './df-module-privacy.interfaces';

declare module '@df/core/service/df-event' {
    interface IEventType {
        // basket
        dfPrivacyChange: DfEventConfig<
            'dfPrivacyChange',
            {
                settings: NDfPrivacy.IPrivacySettings;
            },
            void
        >;
    }
}

@Injectable({
    providedIn: 'root'
})
export class PrivacyService {
    /**
     * Map of allowed cookies
     */
    private _cookies: NDfPrivacy.ICookiesDefinition[] = [];

    /**
     * Takes privacy areas
     */
    areas: NDfPrivacy.AREA[] = [];

    // stringified object
    private get privacySettings(): NDfPrivacy.IPrivacySettings | undefined {
        return this.localStorageService.get('_prm_settings');
    }
    private set privacySettings(value: NDfPrivacy.IPrivacySettings | undefined) {
        // save new date on each settings update
        this.localStorageService.set('_prm_settings', value ? { ...value, saveDate: new Date().toDateString() } : value);
    }

    private get privacyPermissionsChecked(): boolean | undefined {
        return this.localStorageService.get('privacyPermissionsChecked');
    }
    private set privacyPermissionsChecked(value: boolean | undefined) {
        this.localStorageService.set('privacyPermissionsChecked', value);
    }

    private privacyUpdateSubject = new Subject<void>();

    private privacyUpdateSubjectNextDebounced: () => void;

    private onPermissionChange: () => void;

    private cookiesService = inject(CookiesService);
    private logger = inject(Logger);
    private localStorageService = inject(LocalStorageService);
    private timeoutService = inject(TimeoutService);
    private dfEvent = inject(DfEventService);
    private destroyRef = inject(DestroyRef);

    constructor() {
        this.privacyUpdateSubjectNextDebounced = this.timeoutService.debounce(() => this.privacyUpdateSubject.next(), 1000);

        this.onPermissionChange = this.timeoutService.debounce(() => {
            // only if there is any diallowed
            if (PrivacyState.isAnyDisallowed$.value) {
                // iterade all cookies and remove non accepted ones
                each(this.cookiesService.getAll(), (cookieValue: string, cookieName: string) => {
                    // try to recognize cookie
                    const matchedCookie = find(this._cookies, allowedCookie => {
                        // check if cookie name matches
                        if (typeof allowedCookie.test === 'string') {
                            if (allowedCookie.test !== cookieName) {
                                return false;
                            }
                        } else {
                            if (!cookieName.match(allowedCookie.test)) {
                                return false;
                            }
                        }

                        return true;
                    });

                    // if this is matched cookie then take its area, default is personalisation
                    const cookieArea = matchedCookie ? matchedCookie.area : NDfPrivacy.AREA.personalisation;

                    // check if area is allowed and if not then remove cookie
                    if (!this.isAllowed(cookieArea)) {
                        // we will remove both from domain and from .domain
                        this.cookiesService.delete(cookieName);
                        this.logger.log('Cookie ' + cookieName + ' droped cause of privacy restrictions');
                    }
                });
            }
        }, 1000);
    }

    appInitialize() {
        this.checkIfExpired();
        each(NDfPrivacy.AREA, (def, areaKey) => {
            if (!def) {
                return;
            }
            const area = Number(areaKey);
            this.areas.push(area);
            if (Array.isArray(def)) {
                def.forEach(rule => this.registerCookie(area, rule));
            }
        });

        ProcessorService.onReady(() => {
            this.timeoutService.async(
                () => {
                    if (!this.privacyPermissionsChecked) {
                        this.privacyPermissionsChecked = true;
                        this.onChange();
                    }
                    this.subscribeToPrivacyUpdate(() => this.pushPrivacySettings());
                    this.pushPrivacySettings();
                },
                EAsyncPriority.idle,
                { timeout: 20000 }
            );
        });
    }

    /**
     * verify if saved privacy settings are expired and should be removed so customer can confirm again
     */
    checkIfExpired(): void {
        if (NDfPrivacy.AREAS_SETTINGS.expiringAfter) {
            const saveDateString = this.privacySettings?.['saveDate'];

            if (saveDateString && typeof saveDateString === 'string') {
                const saveDate = new Date(saveDateString);
                if (this.isExpired(saveDate)) {
                    this.privacySettings = undefined;
                }
            } else if (!this.needConfirmation) {
                // if no saveDate, we need to reconfirm cookie settings
                this.privacySettings = undefined;
            }
        }
    }

    isExpired(saveDate: Date): boolean | undefined {
        const expiringAfter = NDfPrivacy.AREAS_SETTINGS.expiringAfter;
        if (expiringAfter) {
            const currentDate = new Date();
            const expiryDate = new Date(saveDate);
            expiryDate.setDate(expiryDate.getDate() + NDfPrivacy.AREAS_SETTINGS.expiringAfter);
            return !!(currentDate > expiryDate);
        }
        return undefined;
    }

    /**
     * Adds cookie to area - cookies added are allowed on that level
     * @param {NDfPrivacy.AREA} area
     * @param {string   |    RegExp} test Cookie name or pattern
     */
    registerCookie(area: NDfPrivacy.AREA, test: string | RegExp) {
        this._cookies.push({ test, area });
    }

    /**
     * Removes cookie from privacy area
     * @param {NDfPrivacy.AREA} area
     * @param {string   |    RegExp} test Cookie name or pattern
     */
    unregisterCookie(test: string | RegExp) {
        const index = findIndex(this._cookies, cookie => cookie.test === test);
        if (index > -1) {
            this._cookies.splice(index, 1);
        }
    }

    /**
     * Register observer
     * Observer function are called on privacy settings update
     * @param  {() => void} callback
     */
    subscribeToPrivacyUpdate(...args): Subscription {
        return this.privacyUpdateSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(...args);
    }

    /**
     * Returns privacy as settings object
     */
    get settings(): NDfPrivacy.IPrivacySettings {
        return this.privacySettings || {};
    }

    /**
     * Sets privacy settings
     */
    set settings(settings: NDfPrivacy.IPrivacySettings) {
        this.privacySettings = settings;
    }

    /**
     * Core features are required (non optiobnal)
     * @param  {NDfPrivacy.AREA} area
     * @return {boolean}
     */
    isRequired(area: NDfPrivacy.AREA): boolean {
        return area === NDfPrivacy.AREA.core;
    }

    /**
     * Checks if some preference is allowed
     *
     * It is allowed when undefined too - before you disallow them
     *
     * @param {NDfPrivacy.AREA} area
     */
    isAllowed(area: NDfPrivacy.AREA): boolean {
        const prop = this.settings[area];
        const res = (prop === undefined && NDfPrivacy.AREAS_SETTINGS[area]) || !!prop || this.isRequired(area);
        return res;
    }

    /**
     * Checks if area permission is defined
     *
     * @param {NDfPrivacy.AREA} area
     */
    isDefined(area: NDfPrivacy.AREA): boolean {
        return this.settings[area] !== undefined;
    }

    /**
     * Checks if permissions confirmation is needed
     * (if any permission is not defined)
     */
    get needConfirmation(): boolean {
        return this.areas.find(area => !this.isDefined(area)) !== undefined;
    }

    /**
     * Sets area persmision to allowed (true) or disallowed (state) state
     * @param {NDfPrivacy.AREA} area    Area to set permission sfor
     * @param {boolean}     toState  Is allowed
     */
    setPermission(area: NDfPrivacy.AREA, allowed: boolean) {
        this.settings = { ...this.settings, [area]: allowed };
        this.privacyUpdateSubjectNextDebounced();
        this.onChange();
    }

    private onChange() {
        PrivacyState.isAnyDisallowed$.next(!!find(this.areas, area => !this.isAllowed(area)));
        PrivacyState.isPerformanceAllowed$.next(this.isAllowed(NDfPrivacy.AREA.performance));
        PrivacyState.isPersonalisationAllowed$.next(this.isAllowed(NDfPrivacy.AREA.personalisation));
        PrivacyState.isCoreAllowed$.next(this.isAllowed(NDfPrivacy.AREA.core));
        this.onPermissionChange();
    }

    /**
     * Saves privacy settings with provided data
     * @param { IPrivacyData } data Saved data
     */
    save(data: NDfPrivacy.IPrivacyData): void {
        this.areas.forEach(area => this.setPermission(area, !!data[area]));
    }

    /**
     * Sets all permissions and saves it
     */
    allowAll(): void {
        const data = {};
        this.areas.forEach(area => (data[area] = true));
        this.save(data);
    }

    /**
     * Sets only core permission and saves it
     */
    allowOnlyCore(): void {
        const data = {};
        this.areas.forEach(area => {
            data[area] = area === NDfPrivacy.AREA.core ? true : false;
        });
        this.save(data);
    }

    /**
     * Pushes event that contains information about
     * privacy settings (what cookies we can use)
     */
    protected pushPrivacySettings(): void {
        this.dfEvent.dispatch({
            id: 'dfPrivacyChange',
            data: {
                settings: this.settings
            }
        });
    }
}
